import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import SectionSepratorHeader from "../../components/ui/section-seprator-header";

gsap.registerPlugin(ScrollTrigger);

const SERVICE_DETAILS = [
  {
    id: 1,
    number: "01",
    image:
      "https://cdn.shopify.com/s/files/1/0771/9691/9043/files/business-signs.jpg?v=1773142773",
    title: "Business Signs",
    desc: "Not all signs are created equal. Your signage is often the first impression customers have of your business, and it should communicate professionalism, clarity, and confidence. We design and produce high-quality business signage that reflects your brand identity while remaining durable and easy to maintain. From storefront signs to office displays and directional systems, our solutions help you attract attention, build trust, and guide customers effortlessly to your business.",
  },
  {
    id: 2,
    number: "02",
    image:
      "https://cdn.shopify.com/s/files/1/0771/9691/9043/files/illuminated-signs.jpg?v=1773142774",
    title: "Illuminated Signs",
    desc: "In busy commercial areas, visibility is everything. Illuminated signage ensures your business stands out day and night, making it easier for customers to notice and remember your brand. Our illuminated sign solutions combine modern lighting technologies with durable materials to create vibrant, energy-efficient displays that shine in any environment. Whether you need backlit channel letters, cabinet signs, or LED-lit storefront signage, we design lighting that enhances your brand presence and maximizes visibility.",
  },
  {
    id: 3,
    number: "03",
    image:
      "https://cdn.shopify.com/s/files/1/0771/9691/9043/files/custom-signs.jpg?v=1773142774",
    title: "Custom Signs",
    desc: "Your business is unique, and your signage should reflect that individuality. Our custom signage solutions are designed to capture your brand personality and communicate your message with impact. From concept development to fabrication and installation, we collaborate closely with you to create signs that fit your space, audience, and goals perfectly. Whether it's bold architectural signage, branded displays, or creative feature pieces, we craft custom solutions that leave a lasting impression.",
  },
  {
    id: 4,
    number: "04",
    image:
      "https://cdn.shopify.com/s/files/1/0771/9691/9043/files/led-cove.jpg?v=1773142774",
    title: "LED Cove Lighting Solutions",
    desc: "Lighting can dramatically transform how your space looks and feels. Our LED cove lighting solutions provide elegant, energy-efficient illumination that enhances architectural features and creates a refined atmosphere for your business environment. Designed for both aesthetics and performance, our LED systems deliver consistent brightness, long lifespan, and low energy consumption. Whether highlighting signage, interiors, or exterior features, our lighting solutions help your brand stand out with a modern, polished appearance.",
  },
];

interface ServiceCardProps {
  number: string;
  image: string;
  title: string;
  desc: string;
}

const ServiceCard = ({ number, image, title, desc }: ServiceCardProps) => (
  <div className="flex gap-4 sm:gap-6 items-start">
    <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#4c94ba] min-w-10 sm:min-w-14 lg:min-w-15">
      {number}
    </div>
    <div className="flex flex-col gap-3 sm:gap-4 w-full">
      <img
        src={image}
        alt={title}
        className="w-full h-48 sm:h-64 md:h-72 lg:h-full object-cover rounded-xl"
      />
      <h3 className="text-xl sm:text-2xl font-semibold text-white">{title}</h3>
      <p className="text-white/70 text-sm sm:text-base max-w-lg">{desc}</p>
    </div>
  </div>
);

const NewService = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const leftContentRef = useRef<HTMLDivElement>(null);
  const rightContentRef = useRef<HTMLDivElement>(null);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();

      mm.add("(min-width: 1024px)", () => {
        if (!leftContentRef.current || !rightContentRef.current) return;

        ScrollTrigger.create({
          trigger: sectionRef.current,
          start: "top top",
          end: () =>
            `+=${rightContentRef.current!.offsetHeight - leftContentRef.current!.offsetHeight}`,
          pin: leftContentRef.current,
          pinSpacing: false,
          invalidateOnRefresh: true,
        });
      });

      return () => mm.revert();
    },
    { scope: sectionRef },
  );

  return (
    <section ref={sectionRef} className="w-full bg-[#111212] text-white">
      <div className="flex flex-col px-2.5 md:px-6 mx-auto  py-14 sm:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 sm:gap-14 lg:gap-20 items-start">
          {/* Left Column Wrapper to maintain grid layout during pinning */}
          <div className="lg:h-full">
            <div ref={leftContentRef} className="w-full">
              <SectionSepratorHeader
                className="mb-8 md:mb-12 px-0!"
                title="OUR SERVICES"
                subtitle={
                  <>
                    Built for visibility.
                    <br /> Installed for impact.
                  </>
                }
                description="We specialize in professional sign installation and signage services that help businesses stand out. From custom sign design and fabrication to precise installation and ongoing maintenance."
              />
            </div>
          </div>

          {/* Right Column Content */}
          <div
            ref={rightContentRef}
            className="flex flex-col gap-10 sm:gap-14 lg:gap-20"
          >
            {SERVICE_DETAILS.map((item) => (
              <ServiceCard key={item.id} {...item} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewService;
